import "./tournament-sponsors.scss";
import {
  faFolderOpen,
  faList,
  faPen,
  faPlus,
  faSearch,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  GSInput,
  GSItemList,
  GSListPage,
  GSEmptyList,
  GSActionBar,
} from "golfstatus_react_components";
import { useCallback, useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getTournamentSponsors,
  saveTournamentSponsorOrder,
  selectSponsorList,
  setCurrentSponsor,
  setSponsorList,
  clearNotifications as clearSponsorNotifications,
  selectSponsorNotifications,
  selectLoading,
} from "../../../reducers/sponsorSlice";
import TournamentSponsorLineItem from "./tournament-sponsor-line-item";
import { useMemo } from "react";
import {
  clearNotifications,
  getTournamentSponsorshipTiers,
  selectSponsorshipTierList,
} from "../../../reducers/sponsorshipTierSlice";
import {
  getTournament,
  selectTiersAvailable,
  setTiersAvailable,
} from "../../../reducers/tournamentSlice";
import { useCallbackNotificationAction } from "../../../hooks/notificationHooks";

const TournamentSponsors = (props) => {
  const [search, setSearch] = useState("");
  const match = useMatch("/:tournamentID/sponsors/*");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tiersAvailable = useSelector(selectTiersAvailable);
  const tournamentSponsors = useSelector(selectSponsorList);
  const tournamentSponsorshipTiers = useSelector(selectSponsorshipTierList);
  const loading = useSelector(selectLoading);

  const lockTiers = !tournamentSponsors?.some(
    (sponsor) => sponsor?.sponsorshipType === "leaderboard"
  );

  const notifications = useSelector(selectSponsorNotifications);

  const sponsorshipNotifications = notifications?.filter?.(
    (n) =>
      n?.name === "New Tournament Sponsor" ||
      n?.name === "Tournament Sponsor" ||
      n?.result === "removed"
  );

  const refresh = useCallback(() => {
    dispatch(getTournamentSponsors(match?.params?.tournamentID));
    dispatch(getTournament(match?.params?.tournamentID));
    dispatch(getTournamentSponsorshipTiers(match?.params?.tournamentID));
  }, [dispatch, match?.params?.tournamentID]);

  useCallbackNotificationAction("saved", sponsorshipNotifications, refresh);
  useCallbackNotificationAction("removed", sponsorshipNotifications, refresh);

  const tierSponsors = useMemo(() => {
    if (!tournamentSponsorshipTiers) {
      return;
    }
    const sortedTiers = [...tournamentSponsorshipTiers]?.sort?.(
      (a, b) => a?.sortOrder - b?.sortOrder
    );

    return sortedTiers.map((tier) => {
      const sponsors = tournamentSponsors
        .filter((s) => s?.sponsorshipTierId === tier?.id)
        .sort((a, b) => a?.sortOrder - b?.sortOrder);

      return {
        tier,
        sponsors: sponsors,
      };
    });
  }, [tournamentSponsorshipTiers, tournamentSponsors]);

  useEffect(() => {
    dispatch(getTournamentSponsors(match?.params?.tournamentID));
    dispatch(getTournament(match?.params?.tournamentID));
  }, [dispatch, match?.params?.tournamentID]);

  useEffect(() => {
    if (!lockTiers && !tiersAvailable) {
      dispatch(setTiersAvailable(true));
    }
  }, [dispatch, lockTiers, tiersAvailable]);

  useEffect(() => {
    dispatch(getTournamentSponsorshipTiers(match?.params?.tournamentID));
  }, [dispatch, match?.params?.tournamentID]);

  const addSponsor = () => {
    dispatch(
      setCurrentSponsor({
        sponsorshipType: "hole",
        name: "",
        logo: false,
        mobileLogo: false,
        contactName: "",
        email: "",
      })
    );
    navigate("add");
  };

  const clearSearch = () => {
    setSearch("");
  };

  const printouts = () => {
    navigate("documents");
  };

  const sponsorshipTiers = () => {
    dispatch(clearNotifications());
    navigate("sponsorship-tiers");
  };

  const getPageActions = () => {
    return [
      {
        id: 1,
        buttonTitle: "Add Sponsor",
        actionClick: addSponsor,
        type: "black mobile-icon",
        actionIcon: faPlus,
      },
      {
        id: 1,
        buttonTitle: "Sponsor Tiers",
        actionClick: sponsorshipTiers,
        type: "light-grey mobile-icon",
        actionIcon: faList,
      },
      {
        id: 1,
        buttonTitle: "Documents",
        type: "light-grey mobile-icon",
        actionClick: printouts,
        actionIcon: faFolderOpen,
      },
    ];
  };

  const getSearchActions = () => {
    return [];
  };

  const getDefaultSearch = () => {
    return (
      <GSInput
        textValue={search}
        leftIcon={faSearch}
        rightIcon={faTimesCircle}
        rightIconClick={clearSearch}
        placeholder="Search Sponsors…"
        onChange={(e) => {
          setSearch(e.target.value);
        }}
      ></GSInput>
    );
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Add & Manage Sponsors"
        detail="Sponsors elevate your tournament experience."
        actions={[
          {
            title: "Add Sponsor",
            buttonIcon: faPlus,
            onClick: addSponsor,
            type: "black",
          },
        ]}
      />
    );
  };

  const getEmptySearchMessage = () => {
    return (
      <GSEmptyList
        title="We couldn’t find the sponsor you’re looking for…"
        detail="Try changing the search term or add a new sponsor."
        actions={[
          {
            title: "Clear Search",
            onClick: clearSearch,
            type: "light-grey",
          },
          {
            title: "add Sponsor",
            buttonIcon: faPlus,
            onClick: addSponsor,
            type: "black",
          },
        ]}
      />
    );
  };

  const isSidePanelOpen = () => {
    return match?.params?.["*"] !== "";
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const filterSponsors = (sponList) => {
    const result = [];

    sponList.forEach(({ tier, sponsors }) => {
      const filteredSponsors = sponsors?.filter?.((s) =>
        s?.name?.toLowerCase?.()?.includes?.(search?.toLowerCase())
      );
      if (filteredSponsors?.length > 0) {
        result.push({ tier, sponsors: filteredSponsors });
      }
    });

    return result;
  };

  const reOrderSponsor = (
    sourceSponsor,
    sourceIndex,
    destSponsor,
    destIndex,
    destListID
  ) => {
    const updatedSponsors = [...tournamentSponsors];

    if (sourceSponsor?.sponsorshipTierId !== destListID) {
      return;
    }

    const tierSponsors = updatedSponsors
      .filter(
        (sponsor) =>
          sponsor.sponsorshipTierId === sourceSponsor.sponsorshipTierId
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const [movedSponsor] = tierSponsors.splice(sourceIndex, 1);
    tierSponsors.splice(destIndex, 0, movedSponsor);

    const updatedTierSponsors = tierSponsors.map((sponsor, index) => ({
      ...sponsor,
      sortOrder: index,
    }));

    const reorderedSponsors = updatedSponsors.map((sponsor) => {
      const updatedSponsor = updatedTierSponsors.find(
        (ts) =>
          ts.id === sponsor.id &&
          ts.sponsorshipTierId === sourceSponsor.sponsorshipTierId
      );
      return updatedSponsor || sponsor;
    });

    dispatch(setSponsorList(reorderedSponsors));
    saveOrder(updatedTierSponsors);
  };

  const saveOrder = (sponsorlist) => {
    sponsorlist.forEach(async (sponsor) => {
      dispatch(saveTournamentSponsorOrder(sponsor));
    });
  };

  const openSponsor = (item) => {
    if (isSidePanelOpen()) {
      navigate(`/${match.params.tournamentID}/sponsors`);
      return;
    }
    dispatch(clearSponsorNotifications());
    dispatch(setCurrentSponsor(item))
    navigate(`${item.id}`);
  };

  const editTier = (item) => {
    navigate(`./sponsorship-tiers/${item.id}/edit`);
  };

  const getGroups = () => {
    const searchSponsors = filterSponsors(tierSponsors);

    if (searchSponsors?.length > 0) {
      return searchSponsors.map(({ tier, sponsors }) => {
        let techTier = tier.sponsorshipTierType === "technology";

        let title = "";

        if (!techTier && !tiersAvailable) {
          title = `Sponsors (${sponsors?.length})`;
        } else if (techTier) {
          title = tier?.name;
        } else {
          title = `${tier?.name} (${sponsors?.length})`;
        }

        return {
          list: (
            <div
              className={techTier ? "leaderboard-sponsors" : "hole-sponsors"}
            >
              <GSActionBar
                header={title}
                pageActions={
                  tiersAvailable
                    ? [
                        {
                          actionIcon: faPen,
                          type: "grey mobile-icon",
                          actionClick: () => editTier(tier),
                        },
                      ]
                    : []
                }
              ></GSActionBar>
              <GSItemList
                listID={tier?.id}
                type={
                  techTier
                    ? "vertical x-large-gap list-item selectable"
                    : "vertical list-item selectable"
                }
                items={sponsors}
                itemSelected={openSponsor}
                listItem={(item) => (
                  <TournamentSponsorLineItem
                    {...item}
                  ></TournamentSponsorLineItem>
                )}
                isSortable={!techTier}
                enableStep={false}
                itemMoved={reOrderSponsor}
                topScrollMargin={100}
                bottomScrollMargin={100}
              ></GSItemList>
            </div>
          ),
        };
      });
    }
  };

  const getItemList = () => {
    return (
      <GSItemList
        type="vertical all-sponsors"
        items={getGroups()}
        listItem={(group) => group.list}
        loading={loading?.length > 0}
        loadingMainText="Loading Sponsors..."
        emptyMessage={
          search !== "" && !loading?.length > 0
            ? getEmptySearchMessage()
            : getEmptyListMessage()
        }
      ></GSItemList>
    );
  };

  return (
    <tournament-sponsors>
      <GSListPage
        title="Sponsors"
        getDefaultSearch={getDefaultSearch}
        getPageActions={getPageActions}
        getSearchActions={getSearchActions}
        getItemList={getItemList}
        isSidePanelOpen={isSidePanelOpen}
        getRouter={getRouter}
      ></GSListPage>
    </tournament-sponsors>
  );
};

export default TournamentSponsors;
